/* eslint-disable import/prefer-default-export */
import { watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { setLocale } from '@/locales/i18n';
import { monitor, reportToken, monitorTypes } from '@/utils/monitor';
import {
    DOMAIN_LIST_API, ROUTE_CHOSEN_STORAGE_KEY, getOrigin,
} from '@/config/env';
import { ls } from '@sfe/bone-cache';
import pushEvents from '@bfe/by-gtm';

// uniframe 是否就绪
export const isUniframe = () => window.BybitUniFrame && typeof window.BybitUniFrame.ready === 'function';

// uniframe 已就绪
export const ready = () => {
    if (isUniframe()) return Promise.resolve();
    return new Promise((done) => {
        window.addEventListener('load', () => {
            if (isUniframe()) done();
        });
    });
};

export function useUniFrame() {
    const i18n = useI18n({ useScope: 'global' });
    const store = useStore();

    // 获取切换线路域名列表
    const chosenDomainList = (() => {
        const list = DOMAIN_LIST_API.map((item) => (
            {
                domain: item,
                api2: getOrigin({ type: 'api', domain: item }),
                ws2: getOrigin({ type: 'ws' }),
            }
        ));
        return list.concat([{
            domain: 'auto',
        }]);
    })();

    // Cookie改造后，ROUTE_CHOSEN_STORAGE_KEY做了修改，删除旧Key('by_route_chosen')，新Key是'route_chosen_v2'；
    ls.remove('by_route_chosen');

    const selectedStorageDomain = ls.get(ROUTE_CHOSEN_STORAGE_KEY);

    // 获取当前选中线路data
    const getRealDomain = (newDomain) => {
        const index = chosenDomainList.findIndex((item) => newDomain === item.domain);
        const current = index > -1 ? index : chosenDomainList.length - 1;
        return {
            idx: current,
            item: chosenDomainList[current],
        };
    };

    // 选择线路点击事件
    const changeRoute = (newDomain) => {
        if (selectedStorageDomain) {
            pushEvents('change-route', 'FE-Perf', `${selectedStorageDomain} -> ${newDomain}`);
        }

        ls.set(ROUTE_CHOSEN_STORAGE_KEY, newDomain);
        window.location.reload();
    };

    const initRoute = getRealDomain(selectedStorageDomain);

    const init = () => {
        ready().then(() => {
            window.BybitUniFrame.ready(({ header, footer, adaBot }) => {
                const { LANGUAGE_CHANGE, LOGOUT } = header.events;
                const { LANGUAGE } = header.properties;

                // 修改语言
                header.subscribeEvent(LANGUAGE_CHANGE, (locale) => {
                    setLocale({ locale, redirect: true });
                    adaBot.setProperty(LANGUAGE, locale);
                });

                // 退出登录
                header.subscribeEvent(LOGOUT, () => {
                    store.dispatch('user/LOGOUT').then(() => {
                        reportToken('uni_header_logout');
                        window.location.reload(true);
                    });
                });

                // 设置语言
                header.init({ containerId: '__bybit_uniframe_header__' }).then(() => {
                    monitor.mark('metric_uni_header_ready', monitorTypes.PAGE_START);
                    header.setProperty(LANGUAGE, i18n.locale.value);
                    // 初始化adaBot
                    if (adaBot) {
                        adaBot.init();
                    }
                });

                // 切换线路
                // 当前部分接口接入了，API域名自动切换，等完全接入后，这里的手动选择切换再去掉
                footer.init({ containerId: '__bybit_uniframe_footer__' }).then(() => {
                    // const { ROUTE_SELECT } = footer.events;
                    // const { SERVICE_DOMAIN_LIST, CURRENT_DOMAIN } = footer.properties;
                    // footer
                    //     .setProperty(SERVICE_DOMAIN_LIST, chosenDomainList)
                    //     .setProperty(CURRENT_DOMAIN, initRoute)
                    //     .subscribeEvent(ROUTE_SELECT, (route) => changeRoute(route));
                });
            });

            watch(() => store.state.user.userstate, () => {
                window.BybitUniFrame.ready(({ header, adaBot }) => {
                    const { USER_INFO } = header.properties;
                    if (store.state.user.userstate === -1) {
                        header.setProperty(USER_INFO, {});
                        adaBot.setProperty(USER_INFO, {});
                    } else if (store.state.user.userstate === 1) {
                        header.setProperty(USER_INFO, store.state.user.info);
                        adaBot.setProperty(USER_INFO, store.state.user.info);
                    }
                });
            }, { immediate: true });
        });
    };

    return {
        init,
    };
}
